import { useState } from 'react';
import { usePushSubscription } from '@/hooks/usePushSubscription';
import { detectPlatform, markPromptDismissed, shouldShowPrompt } from '@/lib/push-platform';
import { isGerman } from '@/i18n/locale';
import { Icon, IconTile } from '@/components/ui/icons';

// ════════════════════════════════════════════════════════════════════
// PushPermissionPrompt — inline card on /today.
//
// Self-gating: computes shouldShowPrompt() once on mount and returns
// null if it shouldn't appear. Parent (Home.tsx) renders unconditionally.
//
// Two visual variants based on platform:
//   - STANDARD            two CTAs (Enable / Maybe later)
//   - IOS_SAFARI_NO_PWA   single CTA + Add-to-Home-Screen hint with
//                         inline iOS share-icon SVG
//
// After dismissal (any path that hides the card) a 7-day cooldown
// applies via localStorage so we don't re-pester. On a successful
// "enable" the cooldown is cleared (hook does this).
// ════════════════════════════════════════════════════════════════════

interface Props {
  babyName: string;
}

export function PushPermissionPrompt({ babyName }: Props) {
  const de = isGerman();
  const [visible, setVisible] = useState(() => shouldShowPrompt());
  const [platform] = useState(() => detectPlatform());
  const { enable, status } = usePushSubscription();

  if (!visible) return null;
  if (platform === 'unsupported') return null;

  const dismiss = () => {
    markPromptDismissed();
    setVisible(false);
  };

  const onEnable = async () => {
    await enable();
    // Hide either way — granted hides because enabled; denied/default
    // hides because the cooldown was just stamped inside the hook.
    setVisible(false);
  };

  if (platform === 'ios_safari_no_pwa') {
    return (
      <section className="rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/10 to-accent/5 p-5">
        <div className="flex items-start gap-3">
          <IconTile tone="accent">
            <Icon.Smartphone size={20} />
          </IconTile>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-slate">
              {de ? 'App zum Home-Bildschirm hinzufügen' : 'Add app to home screen'}
            </h3>
            <p className="text-sm text-slate-muted mt-1.5 leading-relaxed">
              {de
                ? 'Damit du Erinnerungen bekommen kannst, füge Alma zu deinem Home-Bildschirm hinzu: Tippe auf das Teilen-Symbol unten und wähle „Zum Home-Bildschirm".'
                : "To receive reminders, add Alma to your home screen: Tap the share icon below and select 'Add to Home Screen'."}
            </p>
            <div className="flex items-center gap-2 mt-3 text-xs text-slate-muted">
              <ShareIcon />
              <Icon.ArrowRight size={12} />
              <span>{de ? '„Zum Home-Bildschirm"' : "'Add to Home Screen'"}</span>
            </div>
            <div className="mt-4">
              <button
                type="button"
                onClick={dismiss}
                className="btn-primary text-sm py-2 px-4"
              >
                {de ? 'Verstanden' : 'Got it'}
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // STANDARD variant
  const busy = status === 'loading';
  return (
    <section className="rounded-2xl border border-primary-100 bg-gradient-to-br from-primary/[0.06] via-cream to-accent/[0.05] p-5">
      <div className="flex items-start gap-3">
        <IconTile tone="primary">
          <Icon.Bell size={20} />
        </IconTile>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-semibold text-slate">
            {de ? 'Wichtige Erinnerungen' : 'Important reminders'}
          </h3>
          <p className="text-sm text-slate-muted mt-1.5 leading-relaxed">
            {de
              ? `Soll ich dich erinnern an ${babyName}s U-Untersuchungen, Impftermine und wöchentliche Entwicklungstipps?`
              : `Should I remind you about ${babyName}'s pediatrician visits, vaccinations and weekly development tips?`}
          </p>
          <div className="mt-4 flex flex-wrap gap-2 items-center">
            <button
              type="button"
              onClick={onEnable}
              disabled={busy}
              className="btn-primary text-sm py-2 px-4 disabled:opacity-60"
            >
              {busy
                ? (de ? 'Einen Moment…' : 'One moment…')
                : (de ? 'Aktivieren' : 'Enable')}
            </button>
            <button
              type="button"
              onClick={dismiss}
              disabled={busy}
              className="btn-ghost text-sm py-2 px-3"
            >
              {de ? 'Vielleicht später' : 'Maybe later'}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// Standard iOS share-icon glyph — rectangle with up-arrow. Inline SVG
// so no asset file needed.
function ShareIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 12v7a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}
